import React, { useCallback } from 'react';

import { AppError } from '@vk-hr-tek/ui/error';
import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { IconButton } from '@vk-hr-tek/ui/IconButton';
import { Icon } from '@vk-hr-tek/ui/Icon';

interface SubmitErrorProps {
  submitError: AppError;
  withDescription?: boolean;
}

/**
 * Компонент для отображения ошибки при отправке формы.
 *
 * @param {SubmitErrorProps} props - Свойства компонента.
 * @param {AppError} props.submitError - Ошибка, возникшая при отправке формы.
 * @param {boolean} [props.withDescription=false] - Флаг для отображения описания ошибки.
 * @returns {JSX.Element} Компонент для отображения ошибки.
 */

export const SubmitError = ({
  submitError,
  withDescription,
}: SubmitErrorProps) => {
  const handleCopy = useCallback(async () => {
    if (submitError.traceId) {
      await navigator.clipboard.writeText(submitError.traceId);
    }
  }, [submitError.traceId]);

  return (
    <Box>
      <Typography color="accent.text.error" variant="body3">
        {submitError && submitError.title
          ? submitError.title
          : 'Что-то пошло не так. Напишите в чат поддержки и сообщите код ошибки'}
      </Typography>
      {withDescription && submitError.description && (
        <Typography color="accent.text.error" variant="body3">
          {submitError.description}
        </Typography>
      )}
      {submitError?.traceId && (
        <Box display="flex" alignItems="center" mt="8" gap="8">
          <Typography color="text.light.tertirary" variant="body3">
            {`Код ошибки: ${submitError.traceId}`}
          </Typography>
          <IconButton type="button" size="small" onClick={handleCopy}>
            <Icon
              name="copy_outline"
              color="original.brand.primary"
              size="20"
            />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
